// Pure-derivation layer for the identity-provider onboarding modal's step 2.
//
// Given the org state, account access, and current checkbox selections, this
// returns the full UI state for every card (checkbox, subtitle, tooltips) plus
// whether the Continue button should be enabled. No DOM access, no `this`, no
// side effects. The {checked, disabled} pair drives all visual states via CSS.

const PRODUCTS = ["itdr", "ispm"]

// Can the provider deliver this product, given the org state and account
// access? Encapsulates compatibility + !alreadyConfigured + canAccess.
function providerCanAddProduct(provider, product, orgState, accountAccess) {
  if (provider === "google" && product === "ispm") return false
  if (provider === "microsoft" && product === "itdr") return !orgState.hasMsItdr && accountAccess.canAccessItdr
  if (provider === "microsoft" && product === "ispm") return !orgState.hasMsIspm && accountAccess.canAccessIspm
  if (provider === "google" && product === "itdr") return !orgState.hasGoogleItdr && accountAccess.canAccessItdr
  return false
}

function deriveProductDisabled(product, orgState, accountAccess) {
  if (product === "itdr") {
    if (!accountAccess.canAccessItdr) return { disabled: true, overlayTooltip: "Start a trial to add Managed ITDR to your account" }
    if (orgState.hasMsItdr && orgState.hasGoogleItdr) return { disabled: true, overlayTooltip: "This organization already has ITDR for all providers" }
  } else if (product === "ispm") {
    if (!accountAccess.canAccessIspm) return { disabled: true, overlayTooltip: "Start a trial to add ISPM to your account" }
    if (orgState.hasMsIspm) return { disabled: true, overlayTooltip: "Already configured for this organization" }
  }
  return { disabled: false, overlayTooltip: "" }
}

function deriveProductSubtitle(product, orgState) {
  if (product === "itdr") {
    if (orgState.hasMsItdr !== orgState.hasGoogleItdr) return "Add a second provider"
  } else if (product === "ispm") {
    if (orgState.hasMsIspm) return "Already configured"
  }
  return ""
}

function deriveProductCardState(product, { orgState, accountAccess }) {
  const { disabled, overlayTooltip } = deriveProductDisabled(product, orgState, accountAccess)
  return { checked: false, disabled, subtitle: deriveProductSubtitle(product, orgState), overlayTooltip }
}

// If exactly one product is enabled-and-visible, force-check and lock it.
// This captures the flag-off legacy behavior (only ITDR visible → auto-selected)
// and generalizes to any org where only one product can be added.
function applySingleProductPreCheck({ itdrProduct, ispmProduct, ispmVisible }) {
  const itdrAvailable = !itdrProduct.disabled
  const ispmAvailable = ispmVisible && !ispmProduct.disabled

  if (itdrAvailable && !ispmAvailable) {
    return {
      itdr: { ...itdrProduct, checked: true, disabled: true, overlayTooltip: "" },
      ispm: ispmProduct
    }
  }
  if (ispmAvailable && !itdrAvailable) {
    return {
      itdr: itdrProduct,
      ispm: { ...ispmProduct, checked: true, disabled: true, overlayTooltip: "" }
    }
  }
  return { itdr: itdrProduct, ispm: ispmProduct }
}

function autoLockTooltip(provider, uniquelyRequired) {
  if (provider === "microsoft") {
    if (uniquelyRequired.includes("ispm")) return "Google Workspace is unavailable for ISPM — Microsoft is your only option"
    if (uniquelyRequired.includes("itdr")) return "Automatically added as second provider for ITDR"
  } else if (provider === "google") {
    if (uniquelyRequired.includes("itdr")) return "Automatically added as second provider for ITDR"
  }
  return ""
}

function blockedTooltip(provider, selections, orgState) {
  if (provider === "microsoft") {
    if (orgState.hasMsItdr && orgState.hasMsIspm) return "Microsoft is already integrated with all products"
    if (selections.itdr && !selections.ispm && orgState.hasMsItdr) return "Already integrated with ITDR — not being re-added"
    if (selections.ispm && !selections.itdr && orgState.hasMsIspm) return "Already integrated with ISPM — not being re-added"
    return "Microsoft is already integrated with all products"
  }
  if (provider === "google") {
    if (orgState.hasGoogleItdr) return "This organization already has Google Workspace ITDR"
    if (selections.ispm && !selections.itdr) return "ISPM is unavailable for Google Workspace"
    return "This organization already has Google Workspace ITDR"
  }
  return ""
}

function microsoftProviderSubtitle(orgState, ispmVisible) {
  if (orgState.hasMsItdr || orgState.hasMsIspm) return "Already authenticated — no sign-in required in the next step"
  if (ispmVisible) return "ITDR and ISPM"
  return ""
}

function deriveProviderState(provider, { orgState, accountAccess, selections, ispmVisible }) {
  const otherProvider = provider === "microsoft" ? "google" : "microsoft"
  const selectedProducts = PRODUCTS.filter(p => selections[p])

  const candidates = selectedProducts.filter(p => providerCanAddProduct(provider, p, orgState, accountAccess))
  const uniquelyRequired = candidates.filter(p => !providerCanAddProduct(otherProvider, p, orgState, accountAccess))
  const fullySaturated = !PRODUCTS.some(p => providerCanAddProduct(provider, p, orgState, accountAccess))
  const anyProductSelected = selectedProducts.length > 0

  const subtitle = provider === "microsoft" ? microsoftProviderSubtitle(orgState, ispmVisible) : ""

  if (uniquelyRequired.length > 0) {
    return {
      checked: true, disabled: true,
      overlayTooltip: autoLockTooltip(provider, uniquelyRequired),
      subtitle
    }
  }
  if (anyProductSelected && candidates.length === 0) {
    return {
      checked: false, disabled: true,
      overlayTooltip: blockedTooltip(provider, selections, orgState),
      subtitle
    }
  }
  if (!anyProductSelected && fullySaturated) {
    return {
      checked: false, disabled: true,
      overlayTooltip: blockedTooltip(provider, selections, orgState),
      subtitle
    }
  }
  return {
    checked: !!selections[provider], disabled: false,
    overlayTooltip: "", subtitle
  }
}

function deriveContinueEnabled({ orgState, selections }) {
  const anyProduct = selections.itdr || selections.ispm
  const anyProvider = selections.microsoft || selections.google
  if (!anyProduct || !anyProvider) return false
  if (selections.ispm && !selections.microsoft) return false
  if (selections.google && !selections.itdr) return false

  const msItdrNew = selections.microsoft && selections.itdr && !orgState.hasMsItdr
  const msIspmNew = selections.microsoft && selections.ispm && !orgState.hasMsIspm
  const googleItdrNew = selections.google && selections.itdr && !orgState.hasGoogleItdr
  return msItdrNew || msIspmNew || googleItdrNew
}

export function deriveStep2UIState({ orgState, accountAccess, selections, ispmVisible }) {
  const rawItdr = deriveProductCardState("itdr", { orgState, accountAccess })
  const rawIspm = deriveProductCardState("ispm", { orgState, accountAccess })

  const preChecked = applySingleProductPreCheck({ itdrProduct: rawItdr, ispmProduct: rawIspm, ispmVisible })

  // When a product card is interactive (not disabled), preserve the user's selection.
  // When disabled, trust the derivation (pre-check sets checked=true; other disable reasons leave it false).
  const itdr = { ...preChecked.itdr, checked: preChecked.itdr.disabled ? preChecked.itdr.checked : !!selections.itdr }
  const ispm = { ...preChecked.ispm, checked: preChecked.ispm.disabled ? preChecked.ispm.checked : !!selections.ispm }

  const effectiveSelections = { ...selections, itdr: itdr.checked, ispm: ispm.checked }

  const microsoft = deriveProviderState("microsoft", { orgState, accountAccess, selections: effectiveSelections, ispmVisible })
  const google = deriveProviderState("google", { orgState, accountAccess, selections: effectiveSelections, ispmVisible })

  const continueEnabled = deriveContinueEnabled({
    orgState,
    selections: { itdr: itdr.checked, ispm: ispm.checked, microsoft: microsoft.checked, google: google.checked }
  })

  return { itdr, ispm, microsoft, google, continueEnabled }
}
